import React, { useState, useEffect, useRef, useCallback } from 'react'
import AutoResizeTextarea from '../shared/AutoResizeTextarea'
import TeamHeroSection from './TeamHeroSection'
import TeamCoursesSection from './TeamCoursesSection'
import TeamMembersSection from './TeamMembersSection'
import TeamAchievementsSection from './TeamAchievementsSection'
import TeamFAQSection from './TeamFAQSection'
import TeamReviewsSection from './TeamReviewsSection'
import TeamPriceCard from './TeamPriceCard'
import MobileBookingBar from './MobileBookingBar'
import BookingModal from './BookingModal'
import PurchaseSuccessModal from './PurchaseSuccessModal'
import TapPayCheckoutModal from './TapPayCheckoutModal'
import LoginModal from './LoginModal'
import InvoiceInfoModal from './InvoiceInfoModal'
import ConsultationFormModal from './ConsultationFormModal'
import MobileCoursePickerModal from './MobileCoursePickerModal'
import FirstBookingOnboardingModal from 'components/Student/FirstBookingOnboardingModal'
import TeamStudentMapSection from './TeamStudentMapSection'
import CommunityFeedSection from './CommunityFeedSection'
import GroupClassList from './GroupClassList'
import GroupClassDetailModal from './GroupClassDetailModal'
import GroupClassEnrollModal from './GroupClassEnrollModal'
import JoinCommunityModal from './JoinCommunityModal'
import ShareButton from './ShareButton'
import WelcomeCouponBanner from './WelcomeCouponBanner'
import DynamicFormFields from '../shared/DynamicFormFields'
import { createApplication, contactTeam, joinCommunity } from '../../utils/teachingTeamApi'
import { trackEvent } from 'utils/analytics'

import { generateColorSystem } from 'utils/colorUtils'
import { mergeBlockOrder } from 'utils/pageBlockDefaults'

// installment is an ECPay-only, checkout-redirect concern (never part of the
// order/enrollment body). Carry the selected period onto the redirect URL; the
// backend still whitelists the value. Shared by all purchase/enroll/pay flows.
const appendInstallment = (url, installment) =>
  installment ? `${url}${url.includes('?') ? '&' : '?'}installment=${encodeURIComponent(installment)}` : url

export default function TeamPublicPageApp({ initialData, teamSlug }) {
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [showApplyModal, setShowApplyModal] = useState(false)
  const [applyMessage, setApplyMessage] = useState('')
  const [applying, setApplying] = useState(false)
  const [applicationStatus, setApplicationStatus] = useState(initialData.applicationStatus || null)
  const [purchasing, setPurchasing] = useState(false)
  const [pendingGiftRecipientId, setPendingGiftRecipientId] = useState(null)
  const [tapPayCheckout, setTapPayCheckout] = useState(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  // Which action sent the reader here, so the dialog can describe it.
  const [loginIntent, setLoginIntent] = useState(null)
  const openLoginModal = (intent) => { setLoginIntent(intent); setShowLoginModal(true) }
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [bookingPreselectedCourseId, setBookingPreselectedCourseId] = useState(null)
  const [claimingTrial, setClaimingTrial] = useState(false)
  const [showContactModal, setShowContactModal] = useState(false)
  const [contactFormResponses, setContactFormResponses] = useState({})
  const [contactFormErrors, setContactFormErrors] = useState({})
  const [contacting, setContacting] = useState(false)
  const [applyFormResponses, setApplyFormResponses] = useState(initialData.recruitmentForm?.previousResponses || {})
  const [applyFormErrors, setApplyFormErrors] = useState({})
  const [recruitmentFormSubmitted, setRecruitmentFormSubmitted] = useState(initialData.recruitmentForm?.hasSubmitted || false)
  const [showConsultationModal, setShowConsultationModal] = useState(false)
  const [consultationMode, setConsultationMode] = useState(null)
  const [consultationCourseContext, setConsultationCourseContext] = useState(null)
  const [consultationMemberId, setConsultationMemberId] = useState(null)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [pendingInvoicePurchase, setPendingInvoicePurchase] = useState(null)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [pendingDiscountPurchase, setPendingDiscountPurchase] = useState(null)
  const [discountPreview, setDiscountPreview] = useState(null)
  const [invoiceDiscount, setInvoiceDiscount] = useState(null)
  const [showMobileCoursePicker, setShowMobileCoursePicker] = useState(false)
  const [coursePickerInitialCourseId, setCoursePickerInitialCourseId] = useState(null)
  const [initialCourseId, setInitialCourseId] = useState(null)
  const [initialCategoryId, setInitialCategoryId] = useState(null)
  const [initialMemberId, setInitialMemberId] = useState(null)
  const [initialShowcaseKey, setInitialShowcaseKey] = useState(null)
  const [groupClasses, setGroupClasses] = useState([])
  const [selectedGroupClass, setSelectedGroupClass] = useState(null)
  const [pendingGroupClassInvoice, setPendingGroupClassInvoice] = useState(null)
  // Set only for the ?pay_enrollment resume flow: routes confirm to the `pay`
  // action (not `create`) and carries the nested-route ids.
  const [pendingPayContext, setPendingPayContext] = useState(null)
  const [enrolling, setEnrolling] = useState(false)
  // Track whether group classes have been loaded at least once (for URL param auto-actions)
  const [groupClassesLoaded, setGroupClassesLoaded] = useState(false)
  // Pending purchase/trial action to auto-resume after login
  const [pendingAction, setPendingAction] = useState(null)

  const { team, courses, courseCategories, members, achievements, reviews, bookingMode, isRecruiting, recruitingDescription, hasInviteCode, isOwner, searchability, intakeForm, trialUsed: initialTrialUsed, studentPins, ownerActivity, translations, community } = initialData
  const recruitmentForm = initialData.recruitmentForm

  // Consultation CTA copy — one fixed i18n label, unified across all consult CTAs
  const consultCtaLabel = translations?.priceCard?.freeConsultation

  // Apply the single consultation CTA label across all consult-CTA translation paths.
  // NOTE: this overrides the consult-CTA label fields below at runtime — editing any
  // of those keys directly has no visible effect; change consultCtaLabel's source
  // (public_page.price_card.free_consultation) instead.
  const consultTranslations = React.useMemo(() => {
    if (!consultCtaLabel || !translations) return translations
    return {
      ...translations,
      hero: { ...translations?.hero, freeConsultation: consultCtaLabel },
      courses: { ...translations?.courses, guidanceButton: consultCtaLabel, courseDetailConsultButton: consultCtaLabel, promotionModal: translations?.promotionLinkModal },
      members: { ...translations?.members, memberConsultButton: consultCtaLabel, promotionModal: translations?.promotionLinkModal },
      priceCard: { ...translations?.priceCard, freeConsultation: consultCtaLabel },
      consultation_nudge: { ...translations?.consultation_nudge, consultButton: consultCtaLabel },
      community: { ...translations?.community, join_community_action: consultCtaLabel, promotionModal: translations?.promotionLinkModal },
      consultation: { ...translations?.consultation, button: consultCtaLabel, title: consultCtaLabel },
    }
  }, [translations, consultCtaLabel])

  const [trialUsed, setTrialUsed] = useState(initialTrialUsed || false)
  const [canInteract, setCanInteract] = useState(community?.canInteract || false)
  const [canPost, setCanPost] = useState(community?.canPost || false)
  const [showJoinCommunityModal, setShowJoinCommunityModal] = useState(false)
  const [guestJoined, setGuestJoined] = useState(false)

  // Track tab revisit — user left and came back (high intent signal)
  useEffect(() => {
    let leftAt = null
    const handleVisibility = () => {
      if (document.hidden) {
        leftAt = Date.now()
      } else if (leftAt) {
        const awaySeconds = Math.round((Date.now() - leftAt) / 1000)
        if (awaySeconds >= 3) {
          trackEvent('tab_revisit', { away_seconds: awaySeconds, team_slug: teamSlug })
        }
        leftAt = null
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])

  // Track text copy — user copying info to share with someone
  useEffect(() => {
    const handleCopy = () => {
      const text = window.getSelection()?.toString()?.trim()
      if (text && text.length > 3 && text.length < 200) {
        trackEvent('text_copied', { text_length: text.length, team_slug: teamSlug })
      }
    }
    document.addEventListener('copy', handleCopy)
    return () => document.removeEventListener('copy', handleCopy)
  }, [])

  // Use state for currentUser so we can update balances after booking
  const [currentUser, setCurrentUser] = useState(initialData.currentUser)

  // Social proof toasts are handled outside React in team-public-page.js

  // Get page design settings with defaults
  const pageDesign = team.pageDesign || {}

  // Dynamic brand color system — falls back to #3366FF if not set
  const COLORS = generateColorSystem(pageDesign.brand_color)

  const blocksOrder = mergeBlockOrder(pageDesign.blocks_order)
  const hasReviews = reviews && reviews.length > 0
  const visibleBlocks = pageDesign.visible_blocks || {
    courses: true,
    members: true,
    reviews: hasReviews,
    faq: false,
    contact: false
  }
  const blockContent = pageDesign.block_content || {}

  // Section visibility tracking — fire once per section when it enters viewport
  const trackedSections = useRef(new Set())
  const sectionRefs = useRef({})
  const getSectionRef = useCallback((blockId) => {
    if (!sectionRefs.current[blockId]) {
      sectionRefs.current[blockId] = React.createRef()
    }
    return sectionRefs.current[blockId]
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.dataset.section
            if (sectionId && !trackedSections.current.has(sectionId)) {
              trackedSections.current.add(sectionId)
              trackEvent('section_view', {
                section_name: sectionId,
                team_slug: teamSlug
              })
            }
          }
        })
      },
      { threshold: 0.3 }
    )

    // Observe all registered section refs
    Object.entries(sectionRefs.current).forEach(([, ref]) => {
      if (ref.current) observer.observe(ref.current)
    })

    return () => observer.disconnect()
  }, [teamSlug])

  // Fetch group classes (series).
  // credentials: 'same-origin' so the session cookie is sent — allows the backend
  // to return per-user enrollment_status when the visitor is logged in.
  useEffect(() => {
    fetch(`/api/team_pages/${teamSlug}/group_classes`, { credentials: 'same-origin' })
      .then(res => res.ok ? res.json() : [])
      .then(data => { setGroupClasses(data); setGroupClassesLoaded(true) })
      .catch(() => { setGroupClasses([]); setGroupClassesLoaded(true) })
  }, [teamSlug])

  // Check if URL has ?apply=1 or ?contact=1 and open modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('apply') === '1' && isRecruiting) {
      setShowApplyModal(true)
    }
    if (params.get('contact') === '1' && currentUser && !isOwner) {
      handleContactClick()
      params.delete('contact')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    }
    if (params.get('join_community') === '1' && currentUser) {
      handleJoinCommunity()
      params.delete('join_community')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    }
    if (params.get('consult') === '1' && !isOwner) {
      if (params.get('source') === 'join') setConsultationMode('join')
      setShowConsultationModal(true)
      params.delete('consult')
      params.delete('source')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    }
  }, [isRecruiting])

  // If the URL lands on #courses (e.g. from a community post trial CTA), scroll the
  // courses section into view once it has painted. Only reads the hash — ref/utm
  // query params are left untouched. The 200ms delay assumes courses are rendered
  // synchronously from initialData (they are); revisit if courses ever move to an
  // async fetch, since #courses would not exist yet when this fires.
  useEffect(() => {
    if (window.location.hash !== '#courses') return
    const timer = setTimeout(() => {
      document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)
    return () => clearTimeout(timer)
  }, [])

  // Check URL params: ?book=courseId (auto-open booking) or ?purchase=success (show toast)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    // Handle ?purchase=success|failed from ECPay redirect
    const purchaseStatus = params.get('purchase')
    if (purchaseStatus) {
      const courseName = params.get('course') || 'Course'
      const lessons = params.get('lessons') || ''
      const purchaseType = params.get('type') || ''
      const sessionCount = params.get('sessions') || ''
      if (purchaseStatus === 'success') {
        trackEvent('ecpay_checkout_success', { course_name: courseName, lessons, type: purchaseType })
        const isGroupClassType = purchaseType === 'group_class' || purchaseType === 'group_class_enrolled'
        const isGift = params.get('gift') === 'true'
        const recipientName = params.get('recipient') || ''
        setSelectedCourse({ name: courseName })
        setSelectedPackage({
          lessonCount: Number(isGroupClassType ? sessionCount : lessons) || 0,
          isGroupClass: isGroupClassType,
          enrolledFromRegistered: purchaseType === 'group_class_enrolled',
          isGift,
          recipientName
        })
        setShowSuccessModal(true)
      } else {
        trackEvent('ecpay_checkout_failed', { course_name: courseName })
        setToastMessage('Purchase failed. Please try again or contact support.')
        setToastType('error')
        setTimeout(() => { setToastMessage(null); setToastType('success') }, 5000)
      }
      // Clean up URL params without adding to browser history, preserve ref and UTM
      params.delete('purchase')
      params.delete('course')
      params.delete('lessons')
      params.delete('type')
      params.delete('sessions')
      params.delete('gift')
      params.delete('recipient')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
      return // Don't process other params after redirect
    }

    // Handle ?book=courseId (auto-open booking modal)
    const bookCourseId = params.get('book')
    if (bookCourseId && currentUser) {
      const courseId = Number(bookCourseId)
      const hasBalanceForCourse = currentUser.balances?.some(
        b => b.courseId === courseId && b.remainingLessons > 0
      )
      if (hasBalanceForCourse) {
        setBookingPreselectedCourseId(courseId)
        setShowBookingModal(true)
      }
    }

    // Handle ?buy=courseId (auto-open course picker for purchase)
    const buyCourseId = params.get('buy')
    if (buyCourseId) {
      setCoursePickerInitialCourseId(Number(buyCourseId))
      setShowMobileCoursePicker(true)
      params.delete('buy')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    }

    // Detect purchase intent: ?package= or ?action=trial
    const courseParam = params.get('course')
    const packageParam = params.get('package')
    const actionParam = params.get('action')
    const memberParam = params.get('member')
    const hasPurchaseIntent = courseParam && (packageParam || actionParam === 'trial')

    // Auto-resume purchase/trial flow after login
    if (hasPurchaseIntent && currentUser) {
      if (actionParam === 'trial') {
        setPendingAction({ type: 'trial', courseId: Number(courseParam) })
      } else {
        setPendingAction({ type: 'purchase', courseId: Number(courseParam), packageId: Number(packageParam) })
      }
      // Clean all intent params from URL
      params.delete('course')
      params.delete('package')
      params.delete('action')
      params.delete('member')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    } else if (hasPurchaseIntent) {
      // Chose a package while logged out. The params stay in the URL on
      // purpose: LoginModal's redirect defaults to the current path and query,
      // so signing in returns here with the intent intact and the branch above
      // resumes it. Stripping them here is what used to drop the choice and
      // send the visitor back to picking a package a second time.
      setInitialCourseId(Number(courseParam))
      openLoginModal(actionParam === 'trial' ? 'trial' : 'purchase')
    } else {
      // No purchase intent — preserve existing modal-open behavior.
      // Clean up any stale intent params.
      params.delete('package')
      params.delete('action')

      if (courseParam) {
        setInitialCourseId(Number(courseParam))
        params.delete('course')
      }

      if (memberParam) {
        setInitialMemberId(Number(memberParam))
        params.delete('member')
      }

      if (courseParam || memberParam) {
        const remaining = params.toString()
        window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
      }
    }

    // Handle ?category=categoryId (auto-open category modal)
    const categoryParam = params.get('category')
    if (categoryParam) {
      setInitialCategoryId(Number(categoryParam))
      params.delete('category')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    }

    // Handle ?showcase=<key> (auto-open teaching-showcase lightbox on that item)
    const showcaseParam = params.get('showcase')
    if (showcaseParam) {
      setInitialShowcaseKey(showcaseParam)
      params.delete('showcase')
      const remaining = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    }
  }, [])

  // Auto-trigger pending purchase/trial action after login
  useEffect(() => {
    if (!pendingAction || !currentUser) return
    const course = courses?.find(c => c.id === pendingAction.courseId)
    if (!course) {
      setPendingAction(null)
      return
    }

    if (pendingAction.type === 'purchase') {
      const pkg = course.packages?.find(p => p.id === pendingAction.packageId)
      if (pkg) {
        // Skip if trial package and trial already used
        if (pkg.isTrial && trialUsed) {
          setPendingAction(null)
          return
        }
        // resumed: the page finishes the click from before login by itself,
        // so team page tracking does not count it as a second action.
        handlePurchase(course, pkg, { resumed: true })
      }
    } else if (pendingAction.type === 'trial') {
      // Skip if trial already used
      if (trialUsed) {
        setPendingAction(null)
        return
      }
      handleClaimTrial(course, { resumed: true })
    }
    setPendingAction(null)
  }, [pendingAction])

  // Opens the payment modal for an EXISTING enrollment. Shared by the emailed
  // ?pay_enrollment link and the brand page's pending-payment button so both
  // entry points resolve the same enrollment, reuse the same order, and offer
  // the same invoice/installment options. Anything that routes a pending
  // enrollment through `create` instead would cancel it and mint a second
  // order, killing the emailed link — that is the bug this replaces.
  //
  // Ownership and payable state are enforced server-side (404 for
  // not-yours/missing, 422 for not-resumable), so there is no client-side
  // ownership check here.
  const openPaymentResume = (enrollmentId, { onSettled } = {}) => {
    let lookupStatus = null
    let lookupError = null
    return fetch(`/api/group_class_enrollments/${enrollmentId}`, { credentials: 'same-origin' })
      .then(async res => {
        lookupStatus = res.status
        if (!res.ok) {
          // The 422 says why (e.g. the payment hold ran out); show that instead
          // of guessing "maybe paid already".
          try { lookupError = (await res.json()).error } catch (e) { /* keep fallback */ }
          throw new Error('lookup_failed')
        }
        return res.json()
      })
      .then(data => {
        onSettled?.()
        // Show the amount frozen on the ORDER, not the class's current price —
        // a pending order can outlive a price change, and ECPay collects the
        // frozen amount. The installment gate keys off this number too.
        setPendingGroupClassInvoice({
          ...data.group_class,
          price: data.order?.amount ?? data.group_class.price,
          currency: data.order?.currency ?? data.group_class.currency
        })
        setPendingPayContext({
          teaching_team_id: data.enrollment.teaching_team_id,
          group_class_id: data.enrollment.group_class_id,
          enrollment_id: data.enrollment.id
        })
        setShowInvoiceModal(true)
      })
      .catch(() => {
        onSettled?.()
        setToastMessage(
          lookupStatus === 422
            ? (lookupError || '這筆報名目前無法付款（可能已完成付款或已處理）。')
            : '找不到你的報名記錄，或你沒有權限付款。'
        )
        setToastType('error')
        setTimeout(() => { setToastMessage(null); setToastType('success') }, 5000)
      })
  }

  // ─── Task 6.4: URL param auto-actions ────────────────────────────────────────
  // Runs once group classes are loaded and currentUser is settled.
  // Params:
  //   ?pay_enrollment=ID  → re-open invoice modal for an existing enrollment
  //   ?enroll_class=ID    → auto-enroll into a group class
  //   ?trial=ID           → auto-enroll into a trial class
  //
  // If user is not logged in, preserve the param through the login redirect
  // so the action fires after OAuth completes.
  useEffect(() => {
    if (!groupClassesLoaded) return

    const params = new URLSearchParams(window.location.search)
    const payEnrollmentId = params.get('pay_enrollment')
    const enrollClassId = params.get('enroll_class')
    const trialId = params.get('trial')

    if (!payEnrollmentId && !enrollClassId && !trialId) return

    // Helper: redirect to login, preserving current URL (including the param)
    const redirectToLogin = () => {
      window.location.href = `/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}`
    }

    // Helper: remove a single param and update URL without reload
    const clearParam = (key) => {
      const p = new URLSearchParams(window.location.search)
      p.delete(key)
      const remaining = p.toString()
      window.history.replaceState({}, '', window.location.pathname + (remaining ? `?${remaining}` : ''))
    }

    // ── ?pay_enrollment=ID ──────────────────────────────────────────────────
    if (payEnrollmentId) {
      if (!currentUser) { redirectToLogin(); return }

      // Resolve the enrollment → group class (for the modal amount/installment)
      // and the ids needed for the nested `pay` action. Ownership + payable
      // state are enforced server-side (404 for not-yours/missing, 422 for
      // not-resumable), so there is no client-side ownership check here.
      openPaymentResume(payEnrollmentId, { onSettled: () => clearParam('pay_enrollment') })
      return
    }

    // ── ?enroll_class=ID ────────────────────────────────────────────────────
    if (enrollClassId) {
      const gcId = Number(enrollClassId)
      const gc = groupClasses.find(g => g.id === gcId)

      if (!gc) {
        clearParam('enroll_class')
        return
      }

      clearParam('enroll_class')
      setSelectedGroupClass(gc)
      return
    }

    // ── ?trial=ID → open parent group class detail modal ──────────────────
    if (trialId) {
      const trialGcId = Number(trialId)
      let parentGc = null
      for (const gc of groupClasses) {
        if (gc.trial_classes?.some(t => t.id === trialGcId)) { parentGc = gc; break }
      }

      clearParam('trial')

      if (parentGc) {
        setSelectedGroupClass(parentGc)
      }
    }
  }, [groupClassesLoaded, currentUser])
  // ─────────────────────────────────────────────────────────────────────────────

  const handleApply = async () => {
    if (!currentUser) {
      window.location.href = `/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}`
      return
    }

    if (!currentUser.isTeacher) {
      alert('Only teachers can apply to join a team.')
      return
    }

    // Validate recruitment form if needed
    if (recruitmentForm && !recruitmentFormSubmitted) {
      const newErrors = {}
      recruitmentForm.fields.forEach(field => {
        if (field.required && !applyFormResponses[field.id]?.trim()) {
          newErrors[field.id] = true
        }
      })
      if (Object.keys(newErrors).length > 0) {
        setApplyFormErrors(newErrors)
        return
      }
    }

    setApplying(true)
    try {
      const formData = (recruitmentForm && !recruitmentFormSubmitted) ? applyFormResponses : null
      await createApplication(team.id, applyMessage, formData)
      setApplicationStatus('pending')
      setShowApplyModal(false)
      setApplyMessage('')
      setApplyFormResponses({})
      if (formData) setRecruitmentFormSubmitted(true)
    } catch (err) {
      alert(err.message || 'Application failed. Please try again later.')
    } finally {
      setApplying(false)
    }
  }

  const handleContactTeam = async () => {
    if (!currentUser) {
      window.location.href = `/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}`
      return
    }

    // If recruitment form needed, validate
    if (recruitmentForm && !recruitmentFormSubmitted) {
      const newErrors = {}
      recruitmentForm.fields.forEach(field => {
        if (field.required && !contactFormResponses[field.id]?.trim()) {
          newErrors[field.id] = true
        }
      })
      if (Object.keys(newErrors).length > 0) {
        setContactFormErrors(newErrors)
        return
      }
    }

    setContacting(true)
    try {
      const formData = (recruitmentForm && !recruitmentFormSubmitted) ? contactFormResponses : null
      await contactTeam(team.id, formData)
      if (formData) setRecruitmentFormSubmitted(true)
      setShowContactModal(false)
      setContactFormResponses({})
      window.location.href = '/dashboard/conversations'
    } catch (err) {
      alert(err.message || 'Failed to contact team. Please try again later.')
    } finally {
      setContacting(false)
    }
  }

  const handleContactClick = () => {
    if (!currentUser) {
      window.location.href = `/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}`
      return
    }
    // If no recruitment form or already submitted, skip modal and contact directly
    if (!recruitmentForm || recruitmentFormSubmitted) {
      setContacting(true)
      contactTeam(team.id, null)
        .then(() => { window.location.href = '/dashboard/conversations' })
        .catch(err => { alert(err.message || 'Failed to contact team. Please try again later.') })
        .finally(() => setContacting(false))
    } else {
      setShowContactModal(true)
    }
  }

  const handleConsultationClick = ({ source, courseContext: ctx, memberId: mid } = {}) => {
    trackEvent('consultation_click', { team_slug: teamSlug, source: source || 'unknown', ...(ctx ? { course_name: ctx } : {}), ...(mid ? { member_id: mid } : {}) })
    if (isOwner) {
      alert(translations?.consultation?.ownerHint || 'This is a free consultation feature for students. Brand members cannot consult their own brand.')
      return
    }
    setConsultationCourseContext(ctx || null)
    setConsultationMemberId(mid || null)
    setShowConsultationModal(true)
  }

  const handleConsultationSubmitted = (result, { hasBooking } = {}) => {
    trackEvent('consultation_submitted', { team_slug: teamSlug })
    if (consultationMode === 'join') {
      window.location.href = '/teams/join?consultation_sent=1'
      return
    }
    setShowConsultationModal(false)
    setCanInteract(true)
    if (community?.postingPolicy === 'teachers_and_students') setCanPost(true)
    const msg = hasBooking
      ? (translations?.consultation?.booking_success || 'Submitted! Booking confirmed. Check your email for details.')
      : (translations?.consultation?.success || 'Submitted! The teacher will contact you via email.')
    setToastMessage(msg)
    setToastType('success')
    setTimeout(() => { setToastMessage(null) }, 5000)
  }

  const isMember = !!(currentUser?.isTeamStudent || currentUser?.teamRole || guestJoined)

  // A teacher-only account cannot be a student, and joining the community makes
  // one (same rule as buying a course). The backend refuses it too.
  const isTeacherOnly = !!(currentUser?.hasTeacherRole && !currentUser?.hasStudentRole)

  const handleJoinCommunity = async () => {
    if (!currentUser) {
      setShowJoinCommunityModal(true)
      return
    }
    if (isTeacherOnly) {
      setToastMessage(translations?.community?.teacher_cannot_join || 'Teacher accounts cannot join the student community, so they cannot like, comment, or follow.')
      setToastType('error')
      setTimeout(() => { setToastMessage(null); setToastType('success') }, 5000)
      return
    }
    try {
      await joinCommunity(team.id)
      setCanInteract(true)
      if (community?.postingPolicy === 'teachers_and_students') setCanPost(true)
      setCurrentUser(prev => prev ? { ...prev, isTeamStudent: true } : prev)
      setToastMessage(translations?.community?.joined_success || 'Welcome! You can now comment, like, and post.')
      setToastType('success')
      setTimeout(() => { setToastMessage(null) }, 5000)
    } catch (err) {
      console.error('Failed to join community:', err)
      setToastMessage(translations?.community?.join_error || 'Something went wrong. Please try again.')
      setToastType('error')
      setTimeout(() => { setToastMessage(null); setToastType('success') }, 5000)
    }
  }

  // Shared non-free checkout dispatch: redirect (append installment), TapPay
  // inline (ECPay-only, no installment), or the ecpay-checkout fallback. Callers
  // handle their own strategy.type === 'free' branch before calling this.
  const goToCheckout = (strategy, data, installment) => {
    if (strategy?.type === 'redirect') {
      window.location.href = appendInstallment(strategy.url, installment)
    } else if (strategy?.type === 'inline') {
      setTapPayCheckout({
        config: strategy.config,
        orderId: data.order.id,
        amount: data.order.amount,
        currency: data.order.currency,
      })
    } else {
      window.location.href = appendInstallment(data.checkout_url || `/api/ecpay/checkout/${data.order.id}`, installment)
    }
  }

  // Execute the actual purchase (create order + redirect to ECPay)
  const executePurchase = async (course, pkg, invoiceData = {}) => {
    const packageToBuy = pkg || course.packages?.find((p, i) => i === 1) || course.packages?.[0]
    if (!packageToBuy) return

    if (purchasing) return
    setPurchasing(true)

    // installment is carried onto the checkout redirect (see appendInstallment),
    // not the order body.
    const { installment, ...orderInvoiceData } = invoiceData

    try {
      const bodyData = { quantity: 1, ...orderInvoiceData }
      if (pendingGiftRecipientId) {
        bodyData.recipient_team_student_id = pendingGiftRecipientId
      }

      const response = await fetch(`/api/course_packages/${packageToBuy.id}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify(bodyData)
      })

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const error = await response.json()
          throw new Error(error.errors?.join(', ') || error.message || 'Failed to create order')
        }
        // Non-JSON error (e.g. Devise login redirect)
        if (response.status === 401 || response.status === 302) {
          openLoginModal('follow')
          setPurchasing(false)
          return
        }
        throw new Error('Failed to create order')
      }

      const data = await response.json()

      const strategy = data.checkout_strategy
      if (strategy?.type === 'free') {
        // Free (or fully coupon-discounted) order — already confirmed server-side,
        // no gateway. Reload into the same success flow the paid path uses so the
        // student sees the success modal AND balances refresh (instead of a silent
        // reload with no feedback).
        const url = new URL(window.location.href)
        url.searchParams.set('purchase', 'success')
        url.searchParams.set('course', course?.name || '')
        url.searchParams.set('lessons', String(packageToBuy.lessonCount || ''))
        window.location.href = url.toString()
      } else {
        // redirect / inline / fallback (purchasing stays true for inline until the modal resolves)
        goToCheckout(strategy, data, installment)
      }
    } catch (err) {
      alert(err.message || 'Failed to create order. Please try again later.')
      setPurchasing(false)
      setPendingGiftRecipientId(null)
    }
  }

  // Check for referral discount before proceeding to payment
  const checkDiscountAndPurchase = async (course, pkg, invoiceData = {}) => {
    if (!currentUser) return executePurchase(course, pkg, invoiceData)

    try {
      const res = await fetch(`/api/course_packages/${pkg.id}/discount_preview`)
      if (res.ok) {
        const data = await res.json()
        if (data.applied > 0) {
          setDiscountPreview({ ...data, courseName: course.name, pkg })
          setPendingDiscountPurchase({ course, pkg, invoiceData })
          setShowDiscountModal(true)
          return
        }
      }
    } catch (err) { /* proceed without discount */ }

    executePurchase(course, pkg, invoiceData)
  }

  const handleDiscountConfirm = () => {
    setShowDiscountModal(false)
    if (pendingDiscountPurchase) {
      executePurchase(
        pendingDiscountPurchase.course,
        pendingDiscountPurchase.pkg,
        pendingDiscountPurchase.invoiceData
      )
      setPendingDiscountPurchase(null)
      setDiscountPreview(null)
    }
  }

  // Direct checkout - intercepts with intake form if needed
  // options.membershipId: if purchase originated from teacher modal, redirect back to teacher modal after login
  const handlePurchase = async (course, pkg, options = {}) => {
    const packageToBuy = pkg || course.packages?.find((p, i) => i === 1) || course.packages?.[0]
    const purchaseSource = options.membershipId ? 'teacher_profile' : (options.source || 'unknown')
    trackEvent('purchase_initiated', {
      course_name: course?.name,
      package_name: packageToBuy?.name,
      is_trial: packageToBuy?.isTrial || false,
      source: purchaseSource,
      team_slug: teamSlug,
      ...(options.resumed && { resumed: true })
    })

    if (!packageToBuy) {
      alert(translations?.alerts?.selectPackage || 'Please select a course package.')
      return
    }

    if (!currentUser) {
      const url = new URL(window.location.href)
      url.searchParams.set('course', String(course.id))
      url.searchParams.set('package', String(packageToBuy.id))
      if (options.membershipId) {
        url.searchParams.set('member', String(options.membershipId))
      }
      window.history.replaceState({}, '', url.toString())
      openLoginModal('purchase')
      return
    }

    // Gift purchase: team members can buy for students regardless of teacher/student role
    const isGiftPurchase = !!options.recipientTeamStudentId
    if (isGiftPurchase) {
      setPendingGiftRecipientId(options.recipientTeamStudentId)
    } else {
      setPendingGiftRecipientId(null)
    }

    if (!isGiftPurchase && currentUser.hasTeacherRole && !currentUser.hasStudentRole) {
      alert(translations?.alerts?.teacherCannotPurchase || 'Teachers cannot purchase courses. Please log in with a student account.')
      return
    }

    // Invoice modal intercept (TWD only, skip for free courses)
    if (packageToBuy.currency === 'TWD' && packageToBuy.totalPrice > 0) {
      // Fetch the discount preview to (a) skip invoice + payment entirely when a
      // coupon makes the order free, and (b) show the discount inline in the modal.
      let preview = null
      try {
        const res = await fetch(`/api/course_packages/${packageToBuy.id}/discount_preview`)
        if (res.ok) preview = await res.json()
      } catch (err) { /* proceed without a discount line */ }

      // Fully discounted → no payment and no invoice needed; confirm directly.
      // Not for gifts: this preview is the BUYER's discount, but a gift order
      // applies the RECIPIENT's, so the buyer's "free" doesn't mean the order is.
      if (!isGiftPurchase && preview && preview.final_amount <= 0) {
        executePurchase(course, packageToBuy)
        return
      }

      setPendingInvoicePurchase({ course, pkg: packageToBuy })
      setInvoiceDiscount(preview && preview.applied > 0 ? preview : null)
      setShowInvoiceModal(true)
      return
    }

    checkDiscountAndPurchase(course, packageToBuy)
  }

  const handleInvoiceConfirm = (invoiceData) => {
    setShowInvoiceModal(false)
    if (pendingPayContext) {
      // Resume flow (?pay_enrollment): the enrollment already exists, so pay the
      // existing order via the `pay` action — NOT `create` (a second active
      // enrollment would violate the active-enrollment uniqueness constraint).
      executeGroupClassPay(pendingPayContext, invoiceData)
      setPendingPayContext(null)
      setPendingGroupClassInvoice(null)
    } else if (pendingGroupClassInvoice) {
      executeGroupClassEnroll(pendingGroupClassInvoice, invoiceData)
      setPendingGroupClassInvoice(null)
    } else if (pendingInvoicePurchase) {
      // Discount was already shown in the invoice modal, so go straight to
      // purchase (the server still applies it at order creation).
      executePurchase(pendingInvoicePurchase.course, pendingInvoicePurchase.pkg, invoiceData)
      setPendingInvoicePurchase(null)
      setInvoiceDiscount(null)
    }
  }

  // Group class enrollment — mirrors handlePurchase flow exactly
  const handleGroupClassEnroll = (groupClass) => {
    if (!currentUser) {
      // Set URL param so after login redirect, modal auto-opens
      // For trials, use the parent group class ID so modal opens with trial visible
      const url = new URL(window.location.href)
      if (groupClass.is_trial) {
        // Find parent — search groupClasses for the one containing this trial
        const parent = groupClasses.find(gc => gc.trial_classes?.some(t => t.id === groupClass.id))
        url.searchParams.set('enroll_class', String(parent?.id || groupClass.id))
      } else {
        url.searchParams.set('enroll_class', String(groupClass.id))
      }
      window.history.replaceState({}, '', url.toString())
      openLoginModal('group_class')
      return
    }

    if (currentUser.hasTeacherRole && !currentUser.hasStudentRole) {
      alert(translations?.alerts?.teacherCannotPurchase || 'Teachers cannot purchase courses. Please log in with a student account.')
      return
    }

    // Already awaiting payment: resume the existing order instead of enrolling
    // again. This branch sits above every other one — including the
    // register_first shortcut and the TWD-only invoice gate — so the brand page
    // offers exactly what the emailed link offers, in every currency. Falling
    // through to `create` here would cancel this enrollment, mint a second
    // order and invalidate the payment link already sitting in the student's
    // inbox. Checked in the shared handler so the class card, the nested trial
    // rows and the detail modal all get it.
    if (groupClass.enrollment_status === 'pending_payment' && groupClass.enrollment_id) {
      openPaymentResume(groupClass.enrollment_id)
      return
    }

    // register_first (non-trial): skip invoice modal, call API directly with empty invoice params
    if (groupClass.enrollment_mode === 'register_first' && !groupClass.is_trial) {
      executeGroupClassEnroll(groupClass, {})
      return
    }

    // Invoice intercept (TWD only, skip for free)
    if ((groupClass.currency || 'TWD') === 'TWD' && groupClass.price > 0) {
      setPendingGroupClassInvoice(groupClass)
      setShowInvoiceModal(true)
      return
    }

    executeGroupClassEnroll(groupClass, {})
  }

  const executeGroupClassEnroll = async (groupClass, invoiceData = {}) => {
    if (enrolling) return
    setEnrolling(true)

    // installment is carried onto the checkout redirect (see appendInstallment),
    // not the enrollment body.
    const { installment, ...enrollBody } = invoiceData

    try {
      const response = await fetch(
        `/api/teaching_teams/${team.id}/group_classes/${groupClass.id}/enrollments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(enrollBody)
        }
      )

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const error = await response.json()
          throw new Error(error.error || 'Failed to enroll')
        }
        if (response.status === 401) {
          openLoginModal('group_class')
          setEnrolling(false)
          return
        }
        throw new Error('Failed to enroll')
      }

      const data = await response.json()
      const strategy = data.checkout_strategy

      if (strategy?.type === 'free') {
        // Determine toast message based on enrollment mode / trial status
        let successMsg
        if (groupClass.enrollment_mode === 'register_first') {
          successMsg = '登記成功'
        } else if (groupClass.is_trial && groupClass.price === 0) {
          successMsg = '試聽報名成功'
        } else {
          successMsg = strategy.message || '報名成功'
        }

        // Show toast
        setToastMessage(successMsg)
        setToastType('success')
        setTimeout(() => { setToastMessage(null) }, 5000)

        // Re-fetch group classes to reflect updated enrollment state
        fetch(`/api/team_pages/${teamSlug}/group_classes`)
          .then(res => res.ok ? res.json() : [])
          .then(updatedData => setGroupClasses(updatedData))
          .catch(() => {})

        // Clear modal state
        setPendingGroupClassInvoice(null)

        setEnrolling(false)
        return
      } else {
        goToCheckout(strategy, data, installment)
      }
    } catch (err) {
      alert(err.message || 'Failed to enroll. Please try again later.')
      setEnrolling(false)
    }
  }

  // Resume payment for an EXISTING pending enrollment (?pay_enrollment flow).
  // Uses the `pay` action instead of `create` (the enrollment already exists),
  // then reuses the same checkout_strategy + appendInstallment handling.
  const executeGroupClassPay = async (payContext, invoiceData = {}) => {
    if (enrolling) return
    setEnrolling(true)

    // installment is carried onto the checkout redirect (see appendInstallment),
    // not the pay body.
    const { installment, ...payBody } = invoiceData

    try {
      const response = await fetch(
        `/api/teaching_teams/${payContext.teaching_team_id}/group_classes/${payContext.group_class_id}/enrollments/${payContext.enrollment_id}/pay`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(payBody)
        }
      )

      if (!response.ok) {
        if (response.status === 401) { openLoginModal('group_class'); setEnrolling(false); return }
        let message = '付款失敗，請稍後再試。'
        try { message = (await response.json()).error || message } catch (e) { /* keep default */ }
        setToastMessage(message)
        setToastType('error')
        setTimeout(() => { setToastMessage(null); setToastType('success') }, 5000)
        setEnrolling(false)
        return
      }

      const data = await response.json()
      // pay is only reachable for a pending, unpaid order, so no 'free' strategy.
      goToCheckout(data.checkout_strategy, data, installment)
    } catch (err) {
      alert(err.message || 'Failed to process payment. Please try again later.')
      setEnrolling(false)
    }
  }


  // Free trial claim flow
  // options.membershipId: if claim originated from teacher modal, redirect back to teacher modal after login
  const handleClaimTrial = async (course, options = {}) => {
    trackEvent('trial_claim_initiated', {
      course_name: course?.name,
      source: options.source || (options.membershipId ? 'teacher_profile' : 'unknown'),
      team_slug: teamSlug,
      ...(options.resumed && { resumed: true })
    })
    if (!currentUser) {
      const url = new URL(window.location.href)
      url.searchParams.set('course', String(course.id))
      url.searchParams.set('action', 'trial')
      if (options.membershipId) {
        url.searchParams.set('member', String(options.membershipId))
      }
      window.history.replaceState({}, '', url.toString())
      openLoginModal('trial')
      return
    }

    if (currentUser.hasTeacherRole && !currentUser.hasStudentRole) {
      alert(translations?.alerts?.teacherCannotClaimTrial || 'Teachers cannot claim trial lessons. Please log in with a student account.')
      return
    }

    executeClaimTrial(course)
  }

  const executeClaimTrial = async (course) => {
    if (claimingTrial) return
    setClaimingTrial(true)

    try {
      const response = await fetch(`/api/team_courses/${course.id}/claim_trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.errors?.join(', ') || 'Failed to claim trial')
      }

      const data = await response.json()

      // Update local balances
      setCurrentUser(prev => {
        if (!prev) return prev
        const existingBalanceIdx = prev.balances?.findIndex(b => b.courseId === course.id)
        const newBalance = {
          courseId: course.id,
          courseName: course.name,
          remainingLessons: data.balance.remainingLessons,
          purchasedLessons: data.balance.purchasedLessons
        }
        const updatedBalances = [...(prev.balances || [])]
        if (existingBalanceIdx >= 0) {
          updatedBalances[existingBalanceIdx] = newBalance
        } else {
          updatedBalances.push(newBalance)
        }
        return { ...prev, isTeamStudent: true, balances: updatedBalances }
      })

      // Open booking modal for this course — trialUsed is set after booking succeeds (in handleBooked)
      setBookingPreselectedCourseId(course.id)
      setShowBookingModal(true)
    } catch (err) {
      alert(err.message || 'Failed to claim trial lesson. Please try again later.')
    } finally {
      setClaimingTrial(false)
    }
  }

  const handleOpenBooking = (preselectedCourseId = null) => {
    setBookingPreselectedCourseId(preselectedCourseId)
    setShowBookingModal(true)
  }

  const handleBooked = (bookedCourseId, bookedCount = 1) => {
    setCurrentUser(prev => {
      if (!prev?.balances) return prev
      return {
        ...prev,
        balances: prev.balances.map(b =>
          b.courseId === bookedCourseId
            ? { ...b, remainingLessons: Math.max(0, b.remainingLessons - bookedCount) }
            : b
        )
      }
    })

    const bookedCourse = courses.find(c => c.id === bookedCourseId)
    if (bookedCourse?.hasTrialPackage) {
      setTrialUsed(true)
    }
  }

  const [showFirstBookingOnboarding, setShowFirstBookingOnboarding] = useState(false)

  const handleCloseBookingModal = ({ firstBooking } = {}) => {
    setShowBookingModal(false)
    setBookingPreselectedCourseId(null)
    if (firstBooking) setShowFirstBookingOnboarding(true)
  }

  const [toastMessage, setToastMessage] = useState(null)
  const [toastType, setToastType] = useState('success')


  const handleCloseSuccess = () => {
    setShowSuccessModal(false)
    setSelectedCourse(null)
    setSelectedPackage(null)
  }

  const handleSuccessBookNow = () => {
    setShowSuccessModal(false)
    setSelectedCourse(null)
    setSelectedPackage(null)
    handleOpenBooking()
  }

  return (
    <div className={`min-h-screen bg-white ${isMember ? 'pb-20' : 'pb-24'} lg:pb-0`}>
      <main className="max-w-6xl mx-auto px-4 py-8">
        <WelcomeCouponBanner
          team={team}
          currentUser={currentUser}
          teamSlug={teamSlug}
          colors={COLORS}
          translations={translations}
          claimable={initialData.welcomeCouponClaimable}
          held={initialData.welcomeCouponHeld}
          onClaim={() => openLoginModal('coupon')}
          suppressed={showLoginModal}
        />
        {/* Two-column layout on desktop */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content - Left Column */}
          <div className="flex-1 min-w-0">
            {/* Hero section - always shown at top (content from Team Settings) */}
            <div ref={getSectionRef('hero')} data-section="hero">
              <TeamHeroSection
                team={team}
                colors={COLORS}
                onConsultationClick={handleConsultationClick}
                consultationEnabled={team.consultation_enabled}
                translations={consultTranslations?.hero}
                reviews={reviews}
                onFollow={handleJoinCommunity}
                isMember={isMember}
                followCtaLabel={translations?.followCta?.label}
                contact={blockContent.contact}
                teamSlug={teamSlug}
                courses={courses}
                onPurchase={(course, pkg, opts) => handlePurchase(course, pkg, { ...opts, source: 'hero_price_anchor' })}
                onClaimTrial={(course, opts) => handleClaimTrial(course, { ...opts, source: 'hero_price_anchor' })}
                trialUsed={trialUsed}
                hasBalance={(currentUser?.balances || []).some((b) => b.remainingLessons > 0)}
              />
            </div>

            {/* Owner-only searchability notice */}
            {isOwner && searchability && !searchability.searchable && (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-amber-800 flex-1">
                  {translations?.searchability_notice || 'Your team is not yet searchable. Please complete the setup in your dashboard.'}
                </p>
                <a
                  href={`/dashboard/teams/${team.id}`}
                  className="text-sm font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap transition-colors"
                >
                  {translations?.go_to_settings || 'Go to Settings'} →
                </a>
              </div>
            )}

            {/* Render blocks in order based on page_design */}
            {blocksOrder.map((blockId) => {
              // Skip hidden blocks
              if (visibleBlocks[blockId] === false) return null

              let content = null
              switch (blockId) {
                case 'members':
                  content = members && members.length > 0 ? (
                    <TeamMembersSection members={members} colors={COLORS} courses={courses} groupClasses={groupClasses} onPurchase={(course, pkg, opts) => handlePurchase(course, pkg, { ...opts, source: 'teacher_profile' })} onClaimTrial={(course, opts) => handleClaimTrial(course, { ...opts, source: 'teacher_profile' })} onOpenGroupClassDetail={(gc) => setSelectedGroupClass(gc)} trialUsed={trialUsed} translations={consultTranslations?.members} teamSlug={teamSlug} initialMemberId={initialMemberId} onConsult={team.consultation_enabled ? handleConsultationClick : undefined} consultationEnabled={team.consultation_enabled} currentUser={currentUser} teamId={team.id} />
                  ) : null
                  break
                case 'achievements':
                  content = achievements && achievements.length > 0 ? (
                    <TeamAchievementsSection achievements={achievements} colors={COLORS} translations={translations?.achievements} teamSlug={teamSlug} initialShowcaseKey={initialShowcaseKey} currentUser={currentUser} teamId={team?.id} shareTranslations={translations?.share} />
                  ) : null
                  break
                case 'reviews':
                  content = reviews && reviews.length > 0 ? (
                    <TeamReviewsSection reviews={reviews} colors={COLORS} translations={translations?.reviews} />
                  ) : null
                  break
                case 'courses':
                  content = (courses && courses.length > 0) || (courseCategories && courseCategories.length > 0) ? (
                    <>
                      <TeamCoursesSection courses={courses} courseCategories={courseCategories} colors={COLORS} onPurchase={(course, pkg, opts) => handlePurchase(course, pkg, { ...opts, source: 'courses_section' })} onClaimTrial={(course, opts) => handleClaimTrial(course, { ...opts, source: 'courses_section' })} trialUsed={trialUsed} translations={consultTranslations?.courses} teamSlug={teamSlug} initialCourseId={initialCourseId} initialCategoryId={initialCategoryId} currentUser={currentUser} teamId={team?.id} onConsult={team.consultation_enabled ? handleConsultationClick : undefined} consultationEnabled={team.consultation_enabled} />
                      {/* Follow CTA — relocated from hero, below courses */}
                      {!isMember && !isTeacherOnly && (
                        <div className="mt-6 bg-gray-50 rounded-xl p-6 text-center">
                          <span className="text-2xl mb-2 block">🔔</span>
                          <p className="text-sm text-gray-600 mb-3">{translations?.hero?.followSectionText || 'Follow this brand to get an email when the teacher sends a notice.'}</p>
                          <button
                            onClick={handleJoinCommunity}
                            className="px-5 py-2 rounded-lg text-sm font-medium cursor-pointer border transition-colors hover:opacity-80"
                            style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                          >
                            🔔 {translations?.followCta?.label || 'Get free learning updates'}
                          </button>
                        </div>
                      )}
                    </>
                  ) : null
                  break
                case 'group_classes':
                  content = groupClasses.length > 0 ? (
                    <GroupClassList
                      groupClasses={groupClasses}
                      colors={COLORS}
                      translations={translations?.groupClasses}
                      teamSlug={teamSlug}
                      currentUser={currentUser}
                      onEnroll={(groupClass) => handleGroupClassEnroll(groupClass)}
                      onOpenDetail={(groupClass) => setSelectedGroupClass(groupClass)}
                    />
                  ) : null
                  break
                case 'faq':
                  content = (blockContent.faq?.items?.length > 0 || translations?.faq?.refundQuestion) ? (
                    <TeamFAQSection faq={blockContent.faq} colors={COLORS} translations={translations?.faq} />
                  ) : null
                  break
                case 'contact':
                  // Social links render inside the hero (SocialLinksRow), not as a body block
                  break
                case 'student_map':
                  content = studentPins && studentPins.length > 0 ? (
                    <TeamStudentMapSection pins={studentPins} colors={COLORS} translations={translations?.studentMap} />
                  ) : null
                  break
                case 'community':
                  if (!community?.hasPosts && !canPost) break
                  content = (
                    <CommunityFeedSection
                      teamId={team.id}
                      teamSlug={teamSlug}
                      colors={COLORS}
                      categories={community?.categories || []}
                      canPost={canPost}
                      canInteract={canInteract}
                      currentUser={currentUser}
                      translations={consultTranslations?.community}
                      onRequestJoin={handleJoinCommunity}
                    />
                  )
                  break
                default:
                  break
              }

              if (!content) return null
              return (
                <div key={blockId} ref={getSectionRef(blockId)} data-section={blockId}>
                  {content}
                </div>
              )
            })}
          </div>

          {/* Sidebar - Right Column (Desktop) */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0">
            {courses && courses.length > 0 && (
              <TeamPriceCard
                courses={courses}
                courseCategories={courseCategories}
                colors={COLORS}
                onPurchase={(course, pkg, opts) => handlePurchase(course, pkg, { ...opts, source: 'sidebar' })}
                onClaimTrial={(course, opts) => handleClaimTrial(course, { ...opts, source: 'sidebar' })}
                onConsult={team.consultation_enabled ? handleConsultationClick : undefined}
                onBooking={() => handleOpenBooking()}
                currentUser={currentUser}
                purchasing={purchasing}
                bookingMode={bookingMode}
                trialUsed={trialUsed}
                ownerActivity={ownerActivity}
                translations={consultTranslations?.priceCard}
                teamId={team?.id}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 mt-8 pt-6 pb-8 text-center text-gray-400">
          {isRecruiting && (!currentUser || currentUser.isTeacher) && (
            <div className="mb-4 text-base">
              <span>{translations?.footer?.recruitingText || 'Interested in joining our teaching brand?'}</span>
              {applicationStatus === 'pending' ? (
                <span className="ml-2 text-yellow-600">Application under review</span>
              ) : applicationStatus === 'accepted' ? (
                <span className="ml-2 text-green-600">{translations?.footer?.joinedText || 'You have joined the team'}</span>
              ) : currentUser ? (
                <button
                  onClick={() => {
                    window.location.href = `/dashboard/explore_teams?apply_team=${teamSlug}`
                  }}
                  className="ml-2 text-blue-500 hover:text-blue-600 transition-colors cursor-pointer"
                >
                  {translations?.footer?.applyToJoin || 'Apply to Join'} →
                </button>
              ) : (
                <form action="/auth/team_apply" method="post" className="inline">
                  <input type="hidden" name="authenticity_token" value={document.querySelector('[name="csrf-token"]')?.content || ''} />
                  <input type="hidden" name="slug" value={teamSlug} />
                  <button
                    type="submit"
                    className="ml-2 text-blue-500 hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    {translations?.footer?.applyToJoin || 'Apply to Join'} →
                  </button>
                </form>
              )}
            </div>
          )}
          {/* Contact Team Button — only for teachers, not students */}
          {currentUser && !isOwner && currentUser.isTeacher && (
            <button
              onClick={handleContactClick}
              disabled={contacting}
              className="mt-3 text-sm text-blue-500 hover:text-blue-600 transition-colors cursor-pointer disabled:opacity-50"
            >
              {contacting ? (translations?.footer?.connecting || 'Connecting...') : `${translations?.footer?.contactTeam || 'Contact Team'} →`}
            </button>
          )}
          <a
            href={`/?ref=${teamSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 group"
          >
            <svg className="w-5 h-5" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="powered-by-logo" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{stopColor: '#4169E1'}} />
                  <stop offset="50%" style={{stopColor: '#6B5DD3'}} />
                  <stop offset="100%" style={{stopColor: '#8B5CF6'}} />
                </linearGradient>
              </defs>
              <rect width="32" height="32" rx="6" fill="url(#powered-by-logo)" />
              <g fill="white">
                <rect x="5" y="7" width="22" height="4" rx="2" />
                <rect x="14" y="7" width="4" height="18" rx="2" />
              </g>
            </svg>
            <span className="text-sm font-medium text-gray-400 group-hover:text-gray-600 transition-colors">
              TutorNetworking
            </span>
          </a>
        </footer>
      </main>

      {/* Booking Modal */}
      {showBookingModal && currentUser?.balances && (
        <BookingModal
          balances={currentUser.balances}
          courses={courses}
          colors={COLORS}
          onClose={handleCloseBookingModal}
          onBooked={handleBooked}
          onPurchaseMore={(courseId) => {
            setShowBookingModal(false)
            setCoursePickerInitialCourseId(courseId)
            setShowMobileCoursePicker(true)
          }}
          preselectedCourseId={bookingPreselectedCourseId}
          userTimezone={currentUser.timezone}
          translations={translations?.bookingModal}
        />
      )}

      {/* First Booking Onboarding Modal */}
      {showFirstBookingOnboarding && (
        <FirstBookingOnboardingModal
          translations={{ bookings: { first_booking_onboarding: translations?.firstBookingOnboarding } }}
          onClose={() => setShowFirstBookingOnboarding(false)}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 w-96 max-w-full">
          <div className={`border rounded-xl p-4 shadow-lg backdrop-blur-sm flex items-start space-x-3 ${
            toastType === 'error'
              ? 'bg-red-50/95 border-red-200'
              : 'bg-green-50/95 border-green-200'
          }`}>
            <div className={`flex-shrink-0 w-6 h-6 ${toastType === 'error' ? 'text-red-600' : 'text-green-600'}`}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                  toastType === 'error'
                    ? 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z'
                    : 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                } />
              </svg>
            </div>
            <div className={`flex-1 min-w-0 font-medium leading-relaxed ${toastType === 'error' ? 'text-red-800' : 'text-green-800'}`}>
              {toastMessage}
            </div>
            <button
              onClick={() => setToastMessage(null)}
              className={`flex-shrink-0 rounded-lg p-1.5 transition-colors hover:bg-white/50 cursor-pointer ${toastType === 'error' ? 'text-red-600' : 'text-green-600'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Purchase Success Modal */}
      {showSuccessModal && selectedCourse && selectedPackage && (
        <PurchaseSuccessModal
          course={selectedCourse}
          selectedPackage={selectedPackage}
          bookingMode={bookingMode}
          welcomeMessage={team.welcome_message}
          onClose={handleCloseSuccess}
          onBookNow={currentUser?.balances?.some(b => b.remainingLessons > 0) ? handleSuccessBookNow : undefined}
          colors={COLORS}
          translations={translations?.purchaseSuccess}
        />
      )}

      {/* Apply to Team Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Apply to Join {team.name}</h2>

            {!currentUser ? (
              <div className="text-center py-4">
                <p className="text-gray-600 mb-4">Please log in to apply to join the team.</p>
                <a
                  href={`/users/sign_in?redirect_to=${encodeURIComponent(window.location.href + '?apply=1')}`}
                  className="inline-block px-6 py-2 text-white rounded-lg font-medium"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  Log In
                </a>
              </div>
            ) : !currentUser.isTeacher ? (
              <div className="text-center py-4">
                <p className="text-gray-600 mb-4">Only teachers can apply to join a team.</p>
                <p className="text-sm text-gray-500">Please complete your teacher profile first.</p>
              </div>
            ) : (
              <>
                {recruitmentForm && !recruitmentFormSubmitted && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-3">Please fill in the following information to help the team get to know you.</p>
                    <DynamicFormFields
                      fields={recruitmentForm.fields}
                      responses={applyFormResponses}
                      errors={applyFormErrors}
                      translations={{ pleaseSelect: translations?.consultation?.pleaseSelect, fieldRequired: translations?.consultation?.fieldRequired }}
                      onChange={(fieldId, value) => {
                        setApplyFormResponses(prev => ({ ...prev, [fieldId]: value }))
                        if (applyFormErrors[fieldId]) {
                          setApplyFormErrors(prev => { const next = { ...prev }; delete next[fieldId]; return next })
                        }
                      }}
                    />
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Self-introduction (optional)
                  </label>
                  <AutoResizeTextarea
                    value={applyMessage}
                    onChange={(e) => setApplyMessage(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={4}
                    placeholder="Briefly introduce yourself so the team leader can get to know you..."
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowApplyModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApply}
                    disabled={applying}
                    className="px-4 py-2 text-white rounded-lg cursor-pointer disabled:opacity-50"
                    style={{ backgroundColor: COLORS.primary }}
                  >
                    {applying ? 'Applying...' : 'Submit Application'}
                  </button>
                </div>
              </>
            )}

            <button
              onClick={() => setShowApplyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {selectedGroupClass && (
        <GroupClassDetailModal
          groupClass={selectedGroupClass}
          colors={COLORS}
          translations={{ ...translations?.groupClasses, promotionModal: translations?.promotionLinkModal }}
          teamSlug={teamSlug}
          onEnroll={(gc) => {
            setSelectedGroupClass(null)
            handleGroupClassEnroll(gc)
          }}
          onEnrollTrial={(trial) => {
            setSelectedGroupClass(null)
            handleGroupClassEnroll(trial)
          }}
          onClose={() => setSelectedGroupClass(null)}
          currentUser={currentUser}
          teamId={team.id}
        />
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        teamLogo={team.logo_url}
        teamName={team.name}
        colors={COLORS}
        translations={translations}
        intent={loginIntent}
      />

      {/* Group Class Enroll Modal — removed, enrollment now goes directly through handleGroupClassEnroll → InvoiceModal → checkout_strategy (same as course purchase) */}

      {/* Contact Team Modal (with recruitment form) */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b" style={{ backgroundColor: '#E6F0FF' }}>
              <h2 className="text-lg font-bold" style={{ color: '#3366FF' }}>{translations?.contactModal?.title || `Contact ${team.name}`}</h2>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-sm text-gray-500 mb-4">{translations?.contactModal?.subtitle || 'After filling in the information below, a conversation with the team will be started automatically.'}</p>
              <DynamicFormFields
                fields={recruitmentForm?.fields || []}
                responses={contactFormResponses}
                errors={contactFormErrors}
                translations={{ pleaseSelect: translations?.consultation?.pleaseSelect, fieldRequired: translations?.consultation?.fieldRequired }}
                onChange={(fieldId, value) => {
                  setContactFormResponses(prev => ({ ...prev, [fieldId]: value }))
                  if (contactFormErrors[fieldId]) {
                    setContactFormErrors(prev => { const next = { ...prev }; delete next[fieldId]; return next })
                  }
                }}
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => { setShowContactModal(false); setContactFormResponses({}); setContactFormErrors({}) }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg cursor-pointer"
                >
                  {translations?.contactModal?.cancel || 'Cancel'}
                </button>
                <button
                  onClick={handleContactTeam}
                  disabled={contacting}
                  className="px-4 py-2 text-white rounded-lg cursor-pointer disabled:opacity-50"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  {contacting ? (translations?.contactModal?.submitting || 'Submitting...') : (translations?.contactModal?.submit || 'Submit and Start Conversation')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Info Modal */}
      <InvoiceInfoModal
        isOpen={showInvoiceModal}
        onClose={() => { setShowInvoiceModal(false); setPendingInvoicePurchase(null); setInvoiceDiscount(null); setPendingGroupClassInvoice(null); setPendingPayContext(null) }}
        onConfirm={handleInvoiceConfirm}
        translations={translations}
        userEmail={currentUser?.email || ''}
        purchaseInfo={pendingInvoicePurchase}
        discount={invoiceDiscount}
        installmentConfig={initialData.checkout}
        amount={pendingGroupClassInvoice?.price}
        currency={pendingGroupClassInvoice?.currency}
      />

      {/* Consultation Nudge Modal */}
      {/* Discount Confirm Modal */}
      {showDiscountModal && discountPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b" style={{ backgroundColor: '#E6F0FF' }}>
              <h2 className="text-lg font-bold" style={{ color: '#3366FF' }}>
                {translations?.discount_confirm?.title || 'Confirm discount'}
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">{translations?.discount_confirm?.course || 'Course'}</span>
                  <span className="text-gray-900">{discountPreview.courseName}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">{translations?.discount_confirm?.lessons || 'Lessons'}</span>
                  <span className="text-gray-900">{discountPreview.pkg.lessonCount} {translations?.discount_confirm?.lesson_unit || 'lessons'}</span>
                </div>
                <div className="border-t border-gray-200 my-2"></div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">{translations?.discount_confirm?.subtotal || 'Subtotal'}</span>
                  <span className="text-gray-900">NT${Math.round(discountPreview.original_amount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm mb-2 text-green-600 font-medium">
                  <span>{translations?.discount_confirm?.referral_discount || 'Referral discount'}</span>
                  <span>-NT${Math.round(discountPreview.applied).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-200 my-2"></div>
                <div className="flex justify-between font-semibold text-lg">
                  <span>{translations?.discount_confirm?.total || 'Amount due'}</span>
                  <span style={{ color: '#3366FF' }}>NT${Math.round(discountPreview.final_amount).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => { setShowDiscountModal(false); setPendingDiscountPurchase(null); setDiscountPreview(null) }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                {translations?.discount_confirm?.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleDiscountConfirm}
                className="px-6 py-2 text-sm text-white rounded-lg font-medium transition-colors cursor-pointer"
                style={{ backgroundColor: '#3366FF' }}
              >
                {translations?.discount_confirm?.proceed || 'Confirm and pay'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TapPay Inline Checkout Modal */}
      {tapPayCheckout && (
        <TapPayCheckoutModal
          config={tapPayCheckout.config}
          orderId={tapPayCheckout.orderId}
          amount={tapPayCheckout.amount}
          currency={tapPayCheckout.currency}
          onSuccess={() => {
            setTapPayCheckout(null)
            setPurchasing(false)
            window.location.href = `${window.location.pathname}?purchase=success`
          }}
          onClose={() => {
            setTapPayCheckout(null)
            setPurchasing(false)
          }}
        />
      )}

      {/* Mobile Bottom Booking Bar */}
      <MobileBookingBar
        courses={courses}
        currentUser={currentUser}
        bookingMode={bookingMode}
        onBooking={() => handleOpenBooking()}
        onPurchase={() => setShowMobileCoursePicker(true)}
        onConsult={team.consultation_enabled ? handleConsultationClick : undefined}
        colors={COLORS}
        translations={consultTranslations?.priceCard}
      />

      {/* Consultation Form Modal */}
      <JoinCommunityModal
        isOpen={showJoinCommunityModal}
        onClose={() => setShowJoinCommunityModal(false)}
        onSuccess={() => {
          setShowJoinCommunityModal(false)
          setGuestJoined(true)
          setToastMessage(translations?.community?.join_modal?.success || translations?.community?.joined_success || 'Welcome! You are now subscribed.')
          setToastType('success')
          setTimeout(() => { setToastMessage(null) }, 5000)
        }}
        teamId={team.id}
        translations={translations?.community?.join_modal}
      />

      <ConsultationFormModal
        isOpen={showConsultationModal}
        onClose={() => { setShowConsultationModal(false); setConsultationMode(null); setConsultationCourseContext(null); setConsultationMemberId(null) }}
        onSubmitted={handleConsultationSubmitted}
        teamId={team.id}
        teamSlug={teamSlug}
        fields={intakeForm?.fields || []}
        colors={COLORS}
        currentUser={currentUser}
        translations={translations}
        consultationEnabled={team.consultation_enabled}
        consultationDuration={team.consultation_duration}
        userTimezone={currentUser?.timezone}
        previousResponses={intakeForm?.previousResponses}
        mode={consultationMode}
        courseContext={consultationCourseContext}
        memberId={consultationMemberId}
      />

      {/* Mobile Course Picker Bottom Sheet */}
      <MobileCoursePickerModal
        isOpen={showMobileCoursePicker}
        onClose={() => { setShowMobileCoursePicker(false); setCoursePickerInitialCourseId(null) }}
        initialCourseId={coursePickerInitialCourseId}
        courses={courses}
        courseCategories={courseCategories}
        colors={COLORS}
        onPurchase={(course, pkg, opts) => handlePurchase(course, pkg, { ...opts, source: 'mobile_picker' })}
        onClaimTrial={(course, opts) => handleClaimTrial(course, { ...opts, source: 'mobile_picker' })}
        purchasing={purchasing}
        trialUsed={trialUsed}
        translations={consultTranslations?.priceCard}
        currentUser={currentUser}
        teamId={team?.id}
      />
    </div>
  )
}
